import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { FiClock, FiCheck, FiX, FiTag, FiChevronLeft, FiCalendar, FiMapPin } from 'react-icons/fi'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import toast from 'react-hot-toast'
import { getPerformance, getPerformanceSeats, getPerformanceTicketTypes, lockSeats, validatePromoCode, createOrder } from '../services/api'
import SeatMap from '../components/SeatMap'
import SectorMap from '../components/SectorMap'
import useCartStore from '../stores/cartStore'

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_KEY || '')

function CheckoutForm({ onSuccess }) {
    const stripe = useStripe()
    const elements = useElements()
    const [processing, setProcessing] = useState(false)

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!stripe || !elements) return

        setProcessing(true)

        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: {
                return_url: `${window.location.origin}/my-tickets`,
            },
            redirect: 'if_required',
        })

        if (error) {
            toast.error(error.message)
            setProcessing(false)
        } else if (paymentIntent?.status === 'succeeded') {
            onSuccess()
        }
    }

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement />
            <button
                type="submit"
                disabled={!stripe || processing}
                className="btn-primary w-full mt-6"
            >
                {processing ? window.t('obrabotka') : window.t('zavarshi_plashtaneto')}
            </button>
        </form>
    )
}

export default function CheckoutPage() {
    const { performanceId } = useParams()
    const navigate = useNavigate()
    const [performance, setPerformance] = useState(null)
    const [seats, setSeats] = useState([])
    const [sectors, setSectors] = useState([])
    const [selectedSector, setSelectedSector] = useState(null)
    const [ticketTypes, setTicketTypes] = useState([])
    const [soldSeatIds, setSoldSeatIds] = useState([])
    const [lockedSeatIds, setLockedSeatIds] = useState([])
    const [loading, setLoading] = useState(true)
    const [promoCodeInput, setPromoCodeInput] = useState('')
    const [clientSecret, setClientSecret] = useState('')
    const [timeRemaining, setTimeRemaining] = useState(null)
    const lockTimerRef = useRef(null)

    // step: 0 = sector selection (multi-sector halls only), 1 = seats, 2 = ticket types, 3 = payment
    const [step, setStep] = useState(1)

    const {
        selectedSeats,
        ticketAssignments,
        assignTicketType,
        promoCode,
        promoDiscount,
        setPromoCode,
        clearPromoCode,
        setPerformance: setCartPerformance,
        clearCart
    } = useCartStore()

    const currentLanguage = localStorage.getItem('app_lang')

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [step])

    useEffect(() => {
        if (promoCode && ticketAssignments && Object.keys(ticketAssignments).length > 0) {
            handleApplyPromoCode()
        }
    }, [ticketAssignments])

    useEffect(() => {
        loadData()
        setCartPerformance(parseInt(performanceId))

        return () => {
            if (lockTimerRef.current) {
                clearInterval(lockTimerRef.current)
            }
        }
    }, [performanceId])

    const loadData = async () => {
        try {
            const [perfRes, seatsRes, typesRes] = await Promise.all([
                getPerformance(performanceId),
                getPerformanceSeats(performanceId),
                getPerformanceTicketTypes(performanceId),
            ])

            setPerformance(perfRes.data.performance)
            setSeats(seatsRes.data.seats || [])
            setSoldSeatIds(seatsRes.data.sold_seat_ids || [])
            setLockedSeatIds(seatsRes.data.locked_seat_ids || [])
            setTicketTypes(typesRes.data)

            const loadedSectors = seatsRes.data.sectors || []
            setSectors(loadedSectors)

            // If hall has more than one sector, start at sector selection step
            if (loadedSectors.length > 1) {
                setStep(0)
            } else {
                setStep(1)
                if (loadedSectors.length === 1) {
                    setSelectedSector(loadedSectors[0])
                }
            }
        } catch (error) {
            toast.error(window.t('greshka_pri_zarezhdane_na_dannite'))
            navigate('/')
        } finally {
            setLoading(false)
        }
    }

    const handleSectorSelect = (sector) => {
        setSelectedSector(sector)
        setStep(1)
    }

    // Seats filtered to the selected sector (if any)
    const visibleSeats = selectedSector
        ? seats.filter(s => s.sector_id === selectedSector.id)
        : seats

    const handleContinueToTicketTypes = async () => {
        if (selectedSeats.length === 0) {
            toast.error(window.t('molya_izberete_pone_edno_myasto'))
            return
        }

        if (lockTimerRef.current) {
            clearInterval(lockTimerRef.current)
        }

        try {
            const res = await lockSeats(parseInt(performanceId), selectedSeats.map(s => s.id))
            if (res.data.errors?.length > 0) { toast.error(res.data.errors[0]); return }
            if (!res.data.success || !res.data.locked_seats?.length) {
                toast.error(window.t('greshka_pri_rezervirane_na_mesta_molya_opitaite_otnovo'))
                return
            }

            const expiresAt = new Date(res.data.locked_seats[0].expires_at)

            const updateTimer = () => {
                const remaining = Math.max(0, Math.floor((expiresAt - new Date()) / 1000))
                setTimeRemaining(remaining)

                if (remaining <= 0) {
                    if (lockTimerRef.current) {
                        clearInterval(lockTimerRef.current)
                        lockTimerRef.current = null
                    }

                    toast.error(window.t('vashata_rezervatsiya_izteche'))
                    clearCart()
                    // navigate(`/checkout/${performanceId}`)
                    setStep(1)
                }
            }

            updateTimer()

            lockTimerRef.current = setInterval(updateTimer, 1000)

            selectedSeats.forEach(seat => { if (!ticketAssignments[seat.id]) assignTicketType(seat.id, ticketTypes[0]?.id) })
            setStep(2)

        } catch (error) {
            toast.error(window.t('greshka_pri_rezervirane_na_mesta_molya_opitaite_otnovo'))
        }
    }

    const handleApplyPromoCode = async () => {
        try {
            const res = await validatePromoCode(promoCodeInput, calculateSubtotal())
            if (res.data.valid) {
                if (!promoCode) {
                    toast.success(window.t('promo_kodat_e_prilojen'))
                }
                setPromoCode(res.data.promo_code.code, res.data.calculated_discount);
            } else {
                toast.error(res.data.message)
            }
        } catch (error) { toast.error(window.t('nevaliden_promo_kod')) }
    }

    const handleContinueToPayment = async () => {
        const hasAllAssigned = selectedSeats.every(seat => ticketAssignments[seat.id]?.ticketTypeId)
        if (!hasAllAssigned) { toast.error(window.t('molya_izberete_tip_bilet_za_vsichki_mesta')); return }
        try {
            const tickets = selectedSeats.map(seat => ({
                seat_id: seat.id,
                ticket_type_id: ticketAssignments[seat.id].ticketTypeId,
                holder_name: ticketAssignments[seat.id].holderName,
            }))
            const res = await createOrder({ performance_id: parseInt(performanceId), tickets, promo_code: promoCode })
            setClientSecret(res.data.client_secret)
            setStep(3)
        } catch (error) { toast.error(error.response?.data?.message || window.t('greshka_pri_sazdavane_na_porachkata')) }
    }

    const handlePaymentSuccess = () => {
        toast.success(window.t('uspeshno_plashtane_vashite_bileti_sa_gotovi'));
        clearCart();
        navigate('/my-tickets')
    }

    const calculateSubtotal = () => {
        if (!performance) return 0
        return selectedSeats.reduce((total, seat) => {
            const ticketTypeId = ticketAssignments[seat.id]?.ticketTypeId
            const ticketType = ticketTypes.find(t => t.id === ticketTypeId)
            if (!ticketType) return total + parseFloat(performance.base_price)
            let price = parseFloat(performance.base_price)
            if (ticketType.is_percentage) price = price * (1 + parseFloat(ticketType.price_modifier) / 100)
            else price = price + parseFloat(ticketType.price_modifier)
            return total + Math.max(0, price)
        }, 0)
    }

    const calculateTotal = () => Math.max(0, calculateSubtotal() - promoDiscount)

    if (loading) {
        return (
            <div className="min-h-screen pt-16 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        )
    }

    // Build step labels — sector selection only shown if >1 sector
    const stepLabels = currentLanguage === 'en'
        ? (sectors.length > 1
            ? ['Select sector', 'Select seats', 'Select tickets', 'Payment']
            : ['Select seats', 'Select tickets', 'Payment'])
        : (sectors.length > 1
            ? ['Избор на сектор', 'Избор на места', 'Избор на билети', 'Плащане']
            : ['Избор на места', 'Избор на билети', 'Плащане']);

    // Map current step to indicator index
    // If >1 sector: step 0=>0, 1=>1, 2=>2, 3=>3
    // If <=1 sector: step 1=>0, 2=>1, 3=>2
    const indicatorStep = sectors.length > 1 ? step : step - 1

    function getSeatSector(seatSectorId) {
        const seatSector = sectors.find(sector => sector.id === seatSectorId)
        return seatSector ? seatSector.name : ''
    }

    return (
        <div className="min-h-screen pt-16 pb-12">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Steps Indicator */}
                <div className="flex items-center justify-center mb-8 flex-wrap gap-y-2">
                    {stepLabels.map((label, i) => (
                        <div key={i} className="flex items-center">
                            <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold ${indicatorStep > i ? 'bg-green-500 text-green-900' : indicatorStep === i ? 'bg-primary-500 text-gray-900' : 'bg-gray-800 text-gray-500'}`}>
                                {indicatorStep > i ? <FiCheck /> : i + 1}
                            </div>
                            <span className={`ml-2 ${indicatorStep >= i ? 'text-white' : 'text-gray-500'}`}>{label}</span>
                            {i < stepLabels.length - 1 && (
                                <div className={`w-12 h-0.5 mx-3 ${indicatorStep > i ? 'bg-green-500' : 'bg-gray-800'}`} />
                            )}
                        </div>
                    ))}
                </div>

                {/* Event Info */}
                <div className="card p-6 mb-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-white">{performance.event?.title}</h1>
                            <div className="flex flex-wrap gap-4 mt-2 text-gray-400">
                                <span>
                                    <FiCalendar className="w-4 h-4 w-4 h-4 inline -mt-1 mr-[5px]" />
                                    {currentLanguage === 'en'
                                        ? new Date(performance.start_time).toLocaleDateString('en-US', {
                                            weekday: 'long',
                                            day: 'numeric',
                                            month: 'long',
                                            year: 'numeric'
                                        })
                                        : new Date(performance.start_time).toLocaleDateString('bg-BG', {
                                            weekday: 'long',
                                            day: 'numeric',
                                            month: 'long',
                                            year: 'numeric'
                                        })
                                    }
                                </span>
                                <span>
                                    <FiClock className="w-4 h-4 w-4 h-4 inline -mt-1 mr-[5px]" />
                                    {currentLanguage === 'en'
                                        ? new Date(performance.start_time).toLocaleTimeString('en-US', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: true
                                        })
                                        : new Date(performance.start_time).toLocaleTimeString('bg-BG', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: false
                                        })
                                    }
                                </span>
                                <span>
                                    <FiMapPin className="w-4 h-4 w-4 h-4 inline -mt-1 mr-[5px]" />
                                    {performance.hall?.name}
                                </span>
                                {selectedSector && sectors.length > 1 && step < 2 && (
                                    <span className="text-primary-400 font-medium">› {selectedSector.name}</span>
                                )}
                            </div>
                        </div>
                        {step < 2 && (
                            <div className="mt-4 md:mt-0 text-right">
                                <div className="text-gray-400 text-sm">{window.t('bazova_tsena')}</div>
                                <div className="text-2xl font-bold text-white">€{performance.base_price}</div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
                    <div className="lg:col-span-1">
                        {/* Step 0: Sector Selection */}
                        {step === 0 && (
                            <div className="card p-6">
                                <h2 className="text-xl font-bold text-white mb-6">{window.t('izberete_sektor')}</h2>
                                <SectorMap
                                    sectors={sectors}
                                    seats={seats}
                                    soldSeatIds={soldSeatIds}
                                    lockedSeatIds={lockedSeatIds}
                                    onSectorSelect={handleSectorSelect}
                                />
                            </div>
                        )}

                        {/* Step 1: Seat Selection */}
                        {step === 1 && (
                            <div className="card p-6">
                                {sectors.length > 1 && (
                                    <button
                                        onClick={() => {
                                            setStep(0)
                                            setSelectedSector(null)
                                        }}
                                        className="flex items-center gap-1 text-gray-400 hover:text-white text-sm mb-4 transition-colors"
                                    >
                                        <FiChevronLeft className="w-4 h-4" /> {window.t('nazad_kam_sektorite')}
                                    </button>
                                )}
                                <h2 className="text-xl font-bold text-white mb-6">{window.t('izberete_vashite_mesta')}</h2>
                                <SeatMap
                                    hall={performance.hall}
                                    seats={visibleSeats}
                                    soldSeatIds={soldSeatIds}
                                    lockedSeatIds={lockedSeatIds}
                                    selectedSector={selectedSector}
                                />
                                {/* 
                                <button
                                    onClick={handleContinueToTicketTypes}
                                    className="btn-primary w-full mt-8"
                                    disabled={selectedSeats.length === 0}
                                >
                                    Продължи ({selectedSeats.length} избрани места)
                                </button> 
                                */}
                            </div>
                        )}

                        {/* Step 2: Ticket Types */}
                        {step === 2 && (
                            <div className="card p-6">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-xl font-bold text-white">{window.t('izberete_tip_bileti')}</h2>
                                    {timeRemaining !== null && (
                                        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${timeRemaining < 60 ? 'bg-red-500/20 text-red-400 animate-pulse' : 'bg-gray-800 text-gray-300'}`}>
                                            <FiClock className="w-4 h-4" />
                                            <span className="font-mono font-bold">{Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')}</span>
                                            <span className="text-sm">{window.t('ostavat')}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-4">
                                    {selectedSeats.map(seat => (
                                        <div key={seat.id} className="bg-gray-800/50 rounded-xl p-4">
                                            <div className="flex justify-between items-center mb-3">
                                                <span className="text-white font-medium">
                                                    {/* {window.t('myasto')}: {seat.row} / {seat.number} */}
                                                    {sectors.length > 1
                                                        ? <span>{getSeatSector(seat.sector_id)} - {window.t('myasto')}: {seat.row} / {seat.number} ({window.t(seat.type)})</span>
                                                        : <span>{window.t('myasto')}: {seat.row} / {seat.number} ({window.t(seat.type)})</span>
                                                    }
                                                </span>
                                                <span className="text-gray-400 text-sm capitalize">{window.t(seat.type)}</span>
                                            </div>
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                                {ticketTypes.map(type => {
                                                    const isSelected = ticketAssignments[seat.id]?.ticketTypeId === type.id
                                                    let price = parseFloat(performance.base_price)
                                                    if (type.is_percentage) {
                                                        price = price * (1 + parseFloat(type.price_modifier) / 100)
                                                    }
                                                    else {
                                                        price = price + parseFloat(type.price_modifier)
                                                    }

                                                    return (
                                                        <button
                                                            key={type.id}
                                                            onClick={() => assignTicketType(seat.id, type.id)}
                                                            className={`p-3 rounded-lg text-sm transition-all ${isSelected ? 'bg-primary-500 text-gray-900 ring-2 ring-primary-300' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                                                        >
                                                            <div className="font-medium">{currentLanguage === 'en' ? (type.name_en || type.name) : type.name}</div>
                                                            <div className="text-xs mt-1 opacity-80">€{parseFloat(price).toFixed(2)}</div>
                                                        </button>
                                                    )
                                                })}
                                            </div>
                                            {ticketTypes.find(t => t.id === ticketAssignments[seat.id]?.ticketTypeId)?.verification_terms && (
                                                <div className="mt-3 text-sm text-yellow-800 bg-yellow-400/10 rounded-lg p-2">
                                                    ⚠️ {currentLanguage === 'en' ? (ticketTypes.find(t => t.id === ticketAssignments[seat.id]?.ticketTypeId)?.verification_terms_en || ticketTypes.find(t => t.id === ticketAssignments[seat.id]?.ticketTypeId)?.verification_terms) : ticketTypes.find(t => t.id === ticketAssignments[seat.id]?.ticketTypeId)?.verification_terms}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Promo Code */}
                                <div className="mt-6 pt-6 border-t border-gray-800">
                                    <label className="label">{window.t('promo_kod')}</label>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <FiTag className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                                            <input type="text" value={promoCodeInput} onChange={(e) => setPromoCodeInput(e.target.value.toUpperCase())} className="input pl-12" placeholder={window.t('vavedete_promo_kod')} disabled={!!promoCode} />
                                        </div>
                                        {promoCode ? (
                                            <button onClick={clearPromoCode} className="btn-secondary px-4"><FiX /></button>
                                        ) : (
                                            <button onClick={handleApplyPromoCode} className="btn-secondary">{window.t('prilozhi')}</button>
                                        )}
                                    </div>
                                    {promoCode && (
                                        <div className="text-green-600 text-sm mt-2">✓ {window.t('kod_e_prilozhen')} "{promoCode}" - {window.t('spestyavate', 'Спестявате')} €{promoDiscount.toFixed(2)}</div>
                                    )}
                                </div>

                                <div className="flex gap-4 mt-8">
                                    <button onClick={() => setStep(1)} className="btn-secondary flex-1">{window.t('nazad')}</button>
                                    <button onClick={handleContinueToPayment} className="btn-primary flex-1">{window.t('kam_plashtane')}</button>
                                </div>
                            </div>
                        )}

                        {/* Step 3: Payment */}
                        {step === 3 && clientSecret && (
                            <div className="card p-6">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-xl font-bold text-white">{window.t('plashtane')}</h2>
                                    {timeRemaining !== null && (
                                        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${timeRemaining < 60 ? 'bg-red-500/20 text-red-400 animate-pulse' : 'bg-gray-800 text-gray-300'}`}>
                                            <FiClock className="w-4 h-4" />
                                            <span className="font-mono font-bold">{Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')}</span>
                                            <span className="text-sm">{window.t('ostavat')}</span>
                                        </div>
                                    )}
                                </div>
                                <Elements stripe={stripePromise} options={{ clientSecret }}>
                                    <CheckoutForm onSuccess={handlePaymentSuccess} />
                                </Elements>
                            </div>
                        )}
                    </div>

                    {/* Order Summary Sidebar */}
                    <div className="lg:col-span-1">
                        <div className="card p-6 sticky top-24">
                            <h3 className="text-lg font-bold text-white mb-4">{window.t('obobshtenie_na_porachkata')}</h3>
                            <div className="space-y-3 text-sm">
                                {selectedSeats.map(seat => {
                                    const ticketType = ticketTypes.find(t => t.id === ticketAssignments[seat.id]?.ticketTypeId)
                                    let price = parseFloat(performance?.base_price || 0)
                                    if (ticketType) {
                                        if (ticketType.is_percentage) {
                                            price = price * (1 + parseFloat(ticketType.price_modifier) / 100)
                                        } else {
                                            price = price + parseFloat(ticketType.price_modifier)
                                        }
                                    }

                                    return (
                                        <div key={seat.id} className="flex justify-between text-gray-300">
                                            {sectors.length > 1
                                                ? <span>{getSeatSector(seat.sector_id)} - {window.t('myasto')}: {seat.row} / {seat.number} ({window.t(seat.type)})</span>
                                                : <span>{window.t('myasto')}: {seat.row} / {seat.number} ({window.t(seat.type)})</span>
                                            }

                                            <span>€{parseFloat(price).toFixed(2)}</span>
                                        </div>
                                    )
                                })}
                            </div>

                            <div className="border-t border-gray-800 my-4 pt-4 space-y-2">
                                <div className="flex justify-between text-gray-400">
                                    <span>{window.t('mezhdinna_suma')}</span>
                                    <span>€{calculateSubtotal().toFixed(2)}</span>
                                </div>
                                {promoDiscount > 0 && (
                                    <div className="flex justify-between text-green-600">
                                        <span>{window.t('otstapka')}</span>
                                        <span>-€{promoDiscount.toFixed(2)}</span>
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-between text-xl font-bold text-white">
                                <span>{window.t('obshto')}</span>
                                <span>€{calculateTotal().toFixed(2)}</span>
                            </div>

                            {step < 2 && (
                                <button
                                    onClick={handleContinueToTicketTypes}
                                    className="btn-primary w-full mt-8"
                                    disabled={selectedSeats.length === 0}
                                >
                                    {window.t('prodalzhi')} ({selectedSeats.length} {window.t('izbrani_mesta')})
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
